import React, { useState, useEffect, useMemo } from 'react';
import JqueryLoad from '../../../JqueryLoad/JqueryLoad';
import Hdashboard from '../../../components/Hdashboard';
import { useNavigate } from 'react-router-dom';
import { admin_earning, checkBalance, ewalletTotals, teacherOrgList } from '../../../api/apiHandler';
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import customStyles from '../../../utils/dataTableStyles';
import { Helmet } from 'react-helmet';
import useDebounce from '../../../utils/useDebounce';
import { withCode } from '../../../utils/dropdownLabel';
import { formatAmount, roundAmount, toAmount } from '../../../utils/formatMoney';
import { downloadEwalletExcel } from '../../../utils/ewalletExcel';

export default function Earning({ isLoader }) {

  const [earningData, setEarningData] = useState([]);
  const [currency, setCurrency] = useState('all');

  // "Total Tax Provided" — moved here from the admin E-Wallet page. It is the
  // HST/GST handed over with the payouts themselves
  // (tbl_payment_activity.hst_gst_amount on RELEASED payouts), which is a
  // different set of rows than this page's listing (payouts still at
  // 'succeeded'), so it cannot be summed from the table below and comes from
  // the same endpoint the E-Wallet cards used — see fetchWalletCards.
  const [taxProvided, setTaxProvided] = useState(0);

  // "Pending Payment" — what the platform still OWES its providers, in CAD.
  //
  // It is the total of the E-Wallet page's "Teacher Earning" column over the
  // sales we have NOT paid out yet: once a payout has gone to the teacher that
  // sale's earning is not owed any more and drops out, and everything else is
  // added up. A refund reduces an earning in proportion rather than cancelling
  // it, so a partly refunded sale still owes part of its earning — the same rule
  // the column itself uses.
  //
  // Note this is NOT the card of the same name on the E-Wallet Balance page
  // (EWallet/EWalletBalance.js). That one is check_balance's pending_balance: the
  // PLATFORM's own cut (30/40%, not the provider's 70/60%), split from
  // available_balance on whether the meeting ended over 7 days ago — i.e. on
  // eligibility for the next payout run rather than on whether we have actually
  // paid. pending_balance is untouched and that page still reads it; this card
  // reads the new pending_balance_cad.
  //
  // Three lines, bucketed the way every E-Wallet card is: USD Total counts only
  // the sales charged in USD, CAD Total only those charged in CAD, and Final
  // Total is both sides in CAD. Each sale is counted in one bucket only, so
  // neither line mixes the two currencies.
  //
  // See admin_model.buildPendingTeacherEarningQuery /
  // pendingTeacherEarningTotals.
  const [pendingPayment, setPendingPayment] = useState({ usd: 0, cad: 0, final: 0 });

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [search, setSearch] = useState('');
  const debounceSearch = useDebounce(search, 750);

  const [teacherOrgOptions, setTeacherOrgOptions] = useState([]);
  const [selectedTeacherOrg, setSelectedTeacherOrg] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    teacherOrgList().then((response) => {
      if (response.code === "1") {
        setTeacherOrgOptions(response.data.map((item) => ({
          value: item.id,
          // Code after the name — see withCode; same format as the E-Wallet page's
          // Teacher / Organization dropdown.
          label: `${item.category === 'organization' ? 'Organization' : 'Teacher'}: ${withCode(item.full_name, item.code_generate)}`
        })));
      } else {
        setTeacherOrgOptions([]);
      }
    });
  }, []);

  useEffect(() => {
    fetchEarningsData();
  }, [currency, debounceSearch, selectedTeacherOrg]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchEarningsData();
    }
  }, [startDate, endDate]);

  const fetchEarningsData = () => {
    let requestData = {};

    if (startDate && endDate) {
      requestData = {
        start_date: startDate,
        end_date: endDate
      };
    }
    if (currency !== 'all') {
      requestData.currency = currency;
    }
    requestData.search = search;
    if (selectedTeacherOrg) {
      requestData.teacherId = selectedTeacherOrg.value;
    }

    isLoader(true)
    admin_earning(requestData)
      .then(response => {
        if (response.code === "1") {
          setEarningData(response?.data);
          isLoader(false)
        } else {
          setEarningData([]);
          isLoader(false)
        }
      })
      .catch(error => {
        setEarningData([]);
        isLoader(false)
      });

    // Kept on the same call path as the listing so the cards always describe the
    // same filters the table was fetched with.
    fetchWalletCards();
  };

  // The two cards that cannot be summed from the table above them —
  // "Total Tax Provided" and "Pending Payment". Both take the same filters as
  // the listing, in the body shape these endpoints want (startDate/endDate
  // rather than start_date/end_date).
  const fetchWalletCards = () => {
    // Shared filter body. `search` is set separately below: the totals endpoint
    // supports it, check_balance does not.
    const filters = {};

    if (startDate && endDate) {
      filters.startDate = startDate;
      filters.endDate = endDate;
    }
    if (currency !== 'all') {
      filters.currency = currency;
    }
    if (selectedTeacherOrg) {
      filters.teacherId = selectedTeacherOrg.value;
    }

    // "Total Tax Provided" is accumulated from the PAYOUT leg only —
    // computeEwalletTotals adds total_tax_paid on direction === 'paid' rows and
    // a received row never touches it — so `mode: 'made'` is all this card needs
    // and the received leg is not queried for nothing.
    const totalsRequest = { ...filters, mode: 'made' };
    if (search) {
      totalsRequest.search = search;
    }

    ewalletTotals(totalsRequest)
      .then(response => {
        setTaxProvided(response.code === "1" ? response?.data?.total_tax_paid : 0);
      })
      .catch(error => {
        setTaxProvided(0);
      });

    // "Pending Payment" comes from a different endpoint — the 7-day release
    // boundary lives in admin_model.check_balance, not in the E-Wallet totals.
    // It honours the currency and teacher/organization filters and the date
    // range, with two differences worth knowing about:
    //   * it has no search condition, so the search box does not narrow this
    //     one card;
    //   * its date range is on the ENROLLMENT date (em.created_at), while the
    //     listing's is on the payment date (pa.updated_at), so on a narrow range
    //     this card can count a slightly different set of sales than the table.
    checkBalance(filters)
      .then(response => {
        const balance = response.code === "1" ? response?.data : null;
        setPendingPayment({
          usd: balance ? balance.pending_balance_usd : 0,
          cad: balance ? balance.pending_balance_cad : 0,
          final: balance ? balance.pending_balance_cad_final : 0,
        });
      })
      .catch(error => {
        setPendingPayment({ usd: 0, cad: 0, final: 0 });
      });
  };

  const columns = [
    {
      name: 'Id',
      selector: (row, index) => index + 1,
      cellExport: (row, index) => index + 1,
      sortable: true,
      width: '75px'
    },
    {
      name: 'Payment Date',
      selector: row => row.payment_date,
      sortable: true,
      cellExport: row => row.payment_date,
    },
    {
      name: 'Learner Name',
      selector: row => row.learner_name,
      sortable: true,
      cellExport: row => row.learner_name,
    },
    {
      name: (
        <>
          Learner Country
        </>
      ),
      selector: row => row.learner_country,
      sortable: true,
      cellExport: row => row.learner_country,
    },
    {
      name: (
        <>
          Learner <br /> Province
        </>
      ),
      selector: row => row.learner_state ? row.learner_state : '-',
      cellExport: row => row.learner_state ? row.learner_state : '-',
      width: '130px',
    },
    {
      name: 'Teacher Name',
      selector: row => row.teacher_name,
      sortable: true,
      cellExport: row => row.teacher_name,
    },
    {
      name: 'Role',
      selector: row => row.teacher_category,
      sortable: true,
      cellExport: row => row.teacher_category,
      width: '100px'
    },
    {
      name: (
        <>
          Teacher Country
        </>
      ),
      selector: row => row.teacher_country,
      cellExport: row => row.teacher_country,
      width: '130px'
    },
    {
      name: (
        <>
          Teacher Province
        </>
      ),
      selector: row => row.teacher_state ? row.teacher_state : '-',
      cellExport: row => row.teacher_state ? row.teacher_state : '-',
      width: '130px',
    },
    {
      name: 'Class Name',
      selector: row => row.activity_title,
      sortable: true,
      cellExport: row => row.activity_title,
      // The data table cuts long text off with "..." by default. "wrap" lets the
      // full class name run onto more than one line instead of being truncated.
      wrap: true,
      minWidth: '220px',
    },
    {
      name: (
        <>
          Class Price<br />In USD
        </>
      ),
      selector: row => row.paid_currency !== 'CAD' ? `$${Number(row?.activity_price ?? 0).toFixed(2)}` : "-",
      sortable: true,
      cellExport: row => row.paid_currency !== 'CAD' ? `$${Number(row?.activity_price ?? 0).toFixed(2)}` : "-",
      style: {
        backgroundColor: '#dceefb'
      }
    },
    // "Net Paid (Excl. Tax)" — the pre-tax amount the learner is left having
    // paid on this sale, with the class part of any refund already deducted.
    // net_paid_usd / net_paid_cad are computed server-side by getAdminEarnings
    // through wallet_model.netBasePaidByLearner, the same function behind the
    // E-Wallet page's column of this name and its "Total Received" card — so one
    // enrollment shows one figure on both pages.
    //
    // It is a LEARNER-PAYMENT figure, not part of the earning split, so it sits
    // beside Class Price rather than among the Teacher / Admin Earning columns.
    {
      name: (
        <>
          Net Paid <br />In USD
        </>
      ),
      selector: row => row.paid_currency !== 'CAD' ? `$${Number(row.net_paid_usd ?? 0).toFixed(2)}` : "-",
      cellExport: row => row.paid_currency !== 'CAD' ? `${Number(row.net_paid_usd ?? 0).toFixed(2)}` : "-",
      sortable: true,
      style: {
        backgroundColor: '#dceefb'
      }
    },
    {
      name: (
        <>
          Teacher Earning<br />In USD
        </>
      ),
      selector: row => row.paid_currency !== 'CAD' ? `$${Number(row.teacher_amount ?? 0).toFixed(2)}` : "-",
      cellExport: row => row.paid_currency !== 'CAD' ? `${Number(row.teacher_amount ?? 0).toFixed(2)}` : "-",
      sortable: true,
      style: {
        backgroundColor: '#dceefb'
      }
    },
    // "Tax Price In USD" — commented out: no tax is charged on a US sale, so the
    // column only ever printed $0.00. GST/HST is charged on the BUYER's province
    // (common.gst_hst_details reads the buyer's own state_id) and a buyer in a
    // Canadian province is billed in CAD, so every taxed row lands in the CAD
    // block instead, where "Tax Price In CAD" still reports it.
    //
    // Kept verbatim so the column can be restored by deleting this comment.
    // NOTE: with no USD tax, "Teacher Earning After Tax In USD" below is by
    // definition the same figure as "Teacher Earning Before Tax In USD".
    // {
    //   name: (
    //     <>
    //       Tax Price<br />In USD
    //     </>
    //   ),
    //   selector: row => row.paid_currency !== 'CAD' ? `$${Number(row.hst_gst_amount ?? 0).toFixed(2)}` : "-",
    //   cellExport: row => row.paid_currency !== 'CAD' ? `${Number(row.hst_gst_amount ?? 0).toFixed(2)}` : "-",
    //   sortable: true,
    //   style: {
    //     backgroundColor: '#dceefb'
    //   }
    // },
    // Titled without the tax wording: no tax is charged on a US sale, so there is
    // no "after tax" for a USD row to be after. The cell still adds
    // hst_gst_amount on, which is 0.00 on these rows, so the figure is unchanged
    // — only the heading is. ("In CAD" keeps its After Tax title, where the tax
    // is real.)
    {
      name: (
        <>
          Teacher Earning<br />In USD
        </>
      ),
      selector: row => row.paid_currency !== 'CAD' ? `$${(Number(row.teacher_amount ?? 0) + Number(row.hst_gst_amount ?? 0)).toFixed(2)}` : "-",
      cellExport: row => row.paid_currency !== 'CAD' ? `${(Number(row.teacher_amount ?? 0) + Number(row.hst_gst_amount ?? 0)).toFixed(2)}` : "-",
      sortable: true,
      style: {
        backgroundColor: '#dceefb'
      }
    },
    {
      name: (
        <>
          Admin Earning In USD
        </>
      ),
      selector: row => row.paid_currency !== 'CAD' ? `$ ${row.admin_amount}` : "-",
      sortable: true,
      width: "125px",
      style: {
        backgroundColor: '#dceefb'
      }
    },
    {
      name: (
        <>
          Class Price<br />In CAD
        </>
      ),
      selector: row => row.paid_currency === 'CAD' ? `$${Number(row?.activity_price_cad ?? 0).toFixed(2)}` : "-",
      sortable: true,
      cellExport: row => row.paid_currency === 'CAD' ? `$${Number(row?.activity_price_cad ?? 0).toFixed(2)}` : "-",
      style: {
        backgroundColor: '#ffffe0'
      }
    },
    // The CAD side of the same figure — see the USD column above.
    {
      name: (
        <>
          Net Paid (Excl. Tax)<br />In CAD
        </>
      ),
      selector: row => row.paid_currency === 'CAD' ? `$${Number(row.net_paid_cad ?? 0).toFixed(2)}` : "-",
      cellExport: row => row.paid_currency === 'CAD' ? `${Number(row.net_paid_cad ?? 0).toFixed(2)}` : "-",
      sortable: true,
      style: {
        backgroundColor: '#ffffe0'
      }
    },
    {
      name: (
        <>
          Teacher Earning Before Tax<br />In CAD
        </>
      ),
      selector: row => row.paid_currency === 'CAD' ? `$${Number(row.teacher_amount_cad ?? 0).toFixed(2)}` : "-",
      cellExport: row => row.paid_currency === 'CAD' ? `${Number(row.teacher_amount_cad ?? 0).toFixed(2)}` : "-",
      sortable: true,
      style: {
        backgroundColor: '#ffffe0'
      }
    },
    {
      name: (
        <>
          Tax Price<br />In CAD
        </>
      ),
      selector: row => row.paid_currency === 'CAD' ? `$${Number(row.hst_gst_amount_cad ?? 0).toFixed(2)}` : "-",
      cellExport: row => row.paid_currency === 'CAD' ? `${Number(row.hst_gst_amount_cad ?? 0).toFixed(2)}` : "-",
      sortable: true,
      style: {
        backgroundColor: '#ffffe0'
      }
    },
    {
      name: (
        <>
          Teacher Earning After Tax<br />In CAD
        </>
      ),
      selector: row => row.paid_currency === 'CAD' ? `$${(Number(row.teacher_amount_cad ?? 0) + Number(row.hst_gst_amount_cad ?? 0)).toFixed(2)}` : "-",
      cellExport: row => row.paid_currency === 'CAD' ? `${(Number(row.teacher_amount_cad ?? 0) + Number(row.hst_gst_amount_cad ?? 0)).toFixed(2)}` : "-",
      sortable: true,
      style: {
        backgroundColor: '#ffffe0'
      }
    },
    {
      name: (
        <>
          Admin Earning In CAD
        </>
      ),
      selector: row => row.paid_currency === 'CAD' ? `$ ${row.admin_amount_cad}` : "-",
      sortable: true,
      width: "125px",
      style: {
        backgroundColor: '#ffffe0'
      }
    },
    {
      name: `Assigned Teacher's Name`,
      selector: row => row.assign_teacher_name,
      sortable: true,
      cellExport: row => row.assign_teacher_name,
    },
    {
      name: 'Platform',
      selector: row => row.type,
      sortable: true,
      cellExport: row => row.type,
    },
    {
      name: (
        <>
          Class Duration
        </>
      ),
      selector: row => row.duration,
      sortable: true,
      cellExport: row => row.duration,
    },
    {
      name: 'Status',
      selector: row => {
        let buttonClass = "bg-secondary";
        let buttonText = row.payment_status;

        if (row.payment_status === "paid") {
          buttonClass = "bg-info text-dark";
        } else if (row.payment_status === "succeeded") {
          buttonClass = "bg-success text-light";
        } else if (row.payment_status === "refunded") {
          buttonClass = "bg-warning";
        }

        return (
          <button className={`${buttonClass} border-0 px-1 fw-500`} style={{ width: "80px", cursor: "default" }}>
            {buttonText}
          </button>
        );
      },
      sortable: true,
      width: "115px"
    },
    {
      name: (
        <>
          Transaction ID
        </>
      ),
      selector: row => row.transaction_id,
      cellExport: row => row.transaction_id,
    }
  ];

  const handleBack = () => {
    navigate(-1);
  };

  const downloadPdf = async () => {
    try {
      isLoader(true)

      const response = await admin_earning({
        start_date: startDate,
        end_date: endDate,
        downloadFlag: true,
        currency: currency !== 'all' ? currency : undefined,
        search: search,
        teacherId: selectedTeacherOrg ? selectedTeacherOrg.value : undefined,
      });

      if (response?.code == "1" && response?.data) {
        const pdfUrl = response?.data;

        const pdfResponse = await fetch(pdfUrl);
        const pdfBlob = await pdfResponse.blob();

        const randomName = `invoice_${Date.now()}.pdf`;

        const pdfFile = new File([pdfBlob], randomName, { type: 'application/pdf' });

        const pdfDataUrl = URL.createObjectURL(pdfFile);

        const downloadLink = document.createElement('a');
        downloadLink.href = pdfDataUrl;
        downloadLink.download = randomName;

        downloadLink.click();

        console.log('Download started.');
        isLoader(false)
      } else {
        isLoader(false)
        console.error('Invalid response or PDF URL not found.');
      }
    } catch (error) {
      isLoader(false)
      console.error('Error downloading PDF:', error);
    }
  };

  // The card totals, split by currency the same way the E-Wallet page splits
  // its own cards (wallet_model.computeEwalletTotals):
  //
  //   USD Total    only the rows CHARGED in USD, summed from their "In USD" field
  //   CAD Total    only the rows CHARGED in CAD,  summed from their "In CAD" field
  //   Final Total  the CAD side plus the USD side converted to CAD
  //
  // Every row lands in exactly ONE of the two buckets, by its currency of
  // record, so neither line mixes the two currencies and nothing is counted
  // twice. Each bucket reads the very field the matching table column renders,
  // which makes a card the exact total of the column beneath it.
  //
  // Final Total is summed as EVERY row's "_cad" mirror rather than
  // `cad + usd * rate`, because those two are the same thing here: the backend
  // sets each _cad field as (USD value x cadRate) for every row regardless of
  // currency, so the mirror of a USD-charged row already IS its converted CAD
  // value. That also means the page never needs the exchange rate itself, and
  // Final Total is on the same basis as the table's "In CAD" columns.
  //
  // Was: one loop adding `admin_amount` and `hst_gst_amount_cad` across ALL
  // rows, which put a CAD-charged row's USD amount into a figure labelled US$.
  const cardTotals = useMemo(() => {
    const totals = {
      earningsUsd: 0,
      earningsCad: 0,
      earningsCadFinal: 0,
      taxCad: 0,
      taxCadFinal: 0,
    };

    (earningData || []).forEach((item) => {
      if (item?.paid_currency === 'CAD') {
        totals.earningsCad += toAmount(item?.admin_amount_cad);
        totals.taxCad += toAmount(item?.hst_gst_amount_cad);
      } else {
        totals.earningsUsd += toAmount(item?.admin_amount);
      }

      totals.earningsCadFinal += toAmount(item?.admin_amount_cad);
      totals.taxCadFinal += toAmount(item?.hst_gst_amount_cad);
    });

    return totals;
  }, [earningData]);

  // =====================================================================
  // "Excel" download — the same statement the "Download" button produces as a
  // PDF, handed over as a .xlsx instead.
  //
  // Every column below mirrors template.admin_earning_invoice heading for
  // heading, in the same order, reading the same field — and that template
  // already mirrors the `columns` above it — so the table, the PDF and the
  // sheet all report one number per cell.
  //
  // The rows come straight from state rather than from a second request: the
  // listing endpoint returns the whole filtered set (there is no server-side
  // paging) and the download endpoint re-runs the very same query with
  // downloadFlag set, so `earningData` already IS the PDF's row set. Same
  // reasoning as the E-Wallet page's Excel download — see ewalletExcel.js.
  // =====================================================================

  // The PDF prints "$12.34" in the currency a row was charged in and "-" in the
  // other one. The sheet keeps that rule but emits a NUMBER so the column can
  // be summed and pivoted; downloadEwalletExcel puts the "$" on with a cell
  // format. The value is the same one the table shows.
  const excelUsdCell = (row, value) => row?.paid_currency === 'CAD' ? '-' : roundAmount(value);
  const excelCadCell = (row, value) => row?.paid_currency === 'CAD' ? roundAmount(value) : '-';

  const excelColumns = [
    { head: 'Id', cell: (row, index) => index + 1 },
    { head: 'Payment Date', cell: (row) => row?.payment_date || '-' },
    { head: 'Learner Name', cell: (row) => row?.learner_name || '-' },
    { head: 'Learner Country', cell: (row) => row?.learner_country || '-' },
    { head: 'Learner Province', cell: (row) => row?.learner_state ? row.learner_state : '-' },
    { head: 'Teacher Name', cell: (row) => row?.teacher_name || '-' },
    { head: 'Role', cell: (row) => row?.teacher_category || '-' },
    { head: 'Teacher Country', cell: (row) => row?.teacher_country || '-' },
    { head: 'Teacher Province', cell: (row) => row?.teacher_state ? row.teacher_state : '-' },
    { head: 'Class Name', cell: (row) => row?.activity_title || '-' },

    // --- USD block ---
    { head: 'Class Price In USD', money: true, cell: (row) => excelUsdCell(row, row?.activity_price) },
    { head: 'Net Paid In USD', money: true, cell: (row) => excelUsdCell(row, row?.net_paid_usd) },
    // Before tax, then after tax. Both carry the SAME heading here because both
    // do in the table and in the PDF: with no tax on a US sale there is no
    // "after tax" for a USD row to be after, so the page dropped the tax wording
    // from both. Kept identical rather than disambiguated so the three views
    // cannot drift apart.
    { head: 'Teacher Earning In USD', money: true, cell: (row) => excelUsdCell(row, row?.teacher_amount) },
    {
      head: 'Teacher Earning In USD',
      money: true,
      cell: (row) => excelUsdCell(row, toAmount(row?.teacher_amount) + toAmount(row?.hst_gst_amount)),
    },
    { head: 'Admin Earning In USD', money: true, cell: (row) => excelUsdCell(row, row?.admin_amount) },

    // --- CAD block ---
    { head: 'Class Price In CAD', money: true, cell: (row) => excelCadCell(row, row?.activity_price_cad) },
    { head: 'Net Paid (Excl. Tax) In CAD', money: true, cell: (row) => excelCadCell(row, row?.net_paid_cad) },
    { head: 'Teacher Earning Before Tax In CAD', money: true, cell: (row) => excelCadCell(row, row?.teacher_amount_cad) },
    { head: 'Tax Price In CAD', money: true, cell: (row) => excelCadCell(row, row?.hst_gst_amount_cad) },
    {
      head: 'Teacher Earning After Tax In CAD',
      money: true,
      cell: (row) => excelCadCell(row, toAmount(row?.teacher_amount_cad) + toAmount(row?.hst_gst_amount_cad)),
    },
    { head: 'Admin Earning In CAD', money: true, cell: (row) => excelCadCell(row, row?.admin_amount_cad) },

    { head: `Assigned Teacher's Name`, cell: (row) => row?.assign_teacher_name || '-' },
    { head: 'Platform', cell: (row) => row?.type || '-' },
    { head: 'Class Duration', cell: (row) => row?.duration || '-' },
    { head: 'Status', cell: (row) => row?.payment_status || '-' },
    { head: 'Transaction ID', cell: (row) => row?.transaction_id || '-' },
  ];

  // The heading and the filter line under the table's title, the same two the
  // PDF prints. The Download buttons only appear once a date range is set, so
  // the "STATEMENT" / "All Dates" fallback is belt and braces — it keeps the
  // sheet from ever being titled "undefined to undefined".
  const excelStatementHeading = () => startDate && endDate
    ? `STATEMENT FOR: ${startDate} to ${endDate}`
    : 'STATEMENT';

  const excelFilterLines = () => {
    const parts = [];
    // The dropdown label already reads "Teacher: Jane Doe (SS123)", so it goes
    // in as it is.
    if (selectedTeacherOrg) parts.push(selectedTeacherOrg.label);
    if (!(startDate && endDate)) parts.push('All Dates');
    if (currency !== 'all') parts.push(`Currency: ${currency}`);
    if (search) parts.push(`Search: ${search}`);
    return parts.length ? [parts.join('  |  ')] : [];
  };

  // The two cards the PDF prints at its foot, reproduced under the sheet's
  // table — read from the same state the cards above the table render, so all
  // three views agree.
  //
  // "Pending Payment" is deliberately NOT here, and is not in the PDF either:
  // it is not a total of any column in this table (check_balance ranges on the
  // enrollment date and has no search condition), so printing it under these
  // rows would invite it to be read as their sum.
  const excelBalanceCards = () => [
    {
      label: 'Total Earnings',
      lines: [
        `USD Total: US$ ${formatAmount(cardTotals.earningsUsd)}`,
        `CAD Total: CAD$ ${formatAmount(cardTotals.earningsCad)}`,
        `Final Total : CAD$ ${formatAmount(cardTotals.earningsCadFinal)}`,
      ],
    },
    // No USD line: GST/HST is only ever billed to a buyer in a Canadian
    // province, and that buyer is charged in CAD, so this card has no USD side.
    // computeEwalletTotals accumulates total_tax_paid from tax_amount_cad alone,
    // which is why CAD Total and Final Total are the SAME figure here — there is
    // no USD side for the final line to convert and add in.
    //
    // The empty first line is load-bearing: this block is a COLUMN GRID, and
    // downloadEwalletExcel fills a row's segments strictly left to right, so
    // without it this card's CAD amount slid back under the column "Total
    // Earnings" uses for its USD Total. The blank holds that column open so CAD
    // lines up under CAD and Final under Final. Same device, for the same
    // reason, as the E-Wallet page's tax card.
    {
      label: 'Total Tax Provided',
      lines: [
        '',
        `CAD Total: CAD$ ${formatAmount(taxProvided)}`,
        `Final Total : CAD$ ${formatAmount(taxProvided)}`,
      ],
    },
  ];

  const downloadExcel = () => {
    downloadEwalletExcel({
      fileName: `admin_earnings_${Date.now()}`,
      sheetName: 'Admin Earnings',
      title: excelStatementHeading(),
      filterLines: excelFilterLines(),
      columns: excelColumns,
      rows: earningData || [],
      balanceCards: excelBalanceCards(),
    });
  };

  return (
    <>
      <JqueryLoad />
      <Hdashboard />
      <Helmet>
        <style>
          {`
            .card-container {
              border: 1px solid #e1dfdf;
              box-shadow: rgba(0, 0, 0, 0.20) 0px 2px 5px;
              border-radius: 10px;
              background-color: #570861;
              color: white;
              transition: transform 0.3s ease, box-shadow 0.3s ease;
            }

            .card-container:hover {
              transform: translateY(-5px);
              box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
            }

            .card-content {
              transition: color 0.3s ease;
            }

            .card-container:hover .card-content {
              /* Lighter shade of white */
              color: #ffffffcc;
            }

            /* The sidebar nav is position:fixed to the viewport, so if this
               page's own content ever scrolls the DOCUMENT horizontally — and
               this table's 26 columns are far wider than the viewport — the nav
               stays pinned while the cards / filters / table slide left
               underneath it. Containing the overflow here and letting only the
               table's own wrapper scroll keeps the sidebar clear of the data.
               Same fix, for the same reason, as the admin E-Wallet page. */
            .dashboard-app {
              overflow-x: hidden;
              max-width: 100%;
            }

            #download_earnings {
              overflow-x: auto;
              max-width: 100%;
              border-radius: 8px;
              border: 1px solid #e1dfdf;
            }
          `}
        </style>
      </Helmet>
      <main>
        <div className="dashboard-app d-lg-flex px-5 mt-1">
          <header className="dashboard-toolbar d-lg-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <a className="menu-toggle align-items-center d-lg-none d-flex">
                <i className="fa fa-bars" />
              </a>
              <a href="index.php" className="d-lg-none d-block brand-logo">
                <img src="./assets/images/logo.png" alt="logo" />
              </a>
            </div>
          </header>
          <span onClick={handleBack} className="mt-3">
            <svg width="10" height="18" viewBox="0 0 10 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.86172 2.225L8.37839 0.75L0.136719 9L8.38672 17.25L9.86172 15.775L3.08672 9L9.86172 2.225Z" fill="#131313" ></path>
            </svg>
            <span className="ms-2">Back</span>
          </span>

          {/* The commented-out "Download PDF" / "Download Excel" placeholders
              that used to sit beside this title are gone: both are now real
              buttons down in the filter row, next to the filters they apply. */}
          <div className="d-flex justify-content-between title mt-2 mb-2">
            <p className="fs-3 fw-500">My Earnings</p>
          </div>

          {/* Four cards now, so the row wraps on narrower screens instead of
              squeezing them off the edge — same flex-wrap + gap-3 layout and the
              same col-lg-2 col-md-3 card width the E-Wallet page uses for its
              own card row. The gap replaces the mx-3 the middle card carried. */}
          <div className='d-flex flex-wrap gap-3'>
            {/* Total of the "Admin Earning" columns, in the same three-line shape
                the E-Wallet page's cards use — see cardTotals for how each line
                is bucketed. */}
            <div className='col-lg-2 col-md-3 mt-3 p-4 card-container'>
              <div className='content card-content'>
                <p className='fs-5 fw-600'>Total Earnings</p>
                <p className='fs-6 fw-500 mt-3'>USD Total: US$ {formatAmount(cardTotals.earningsUsd)}</p>
                <p className='fs-6 fw-500'>CAD Total: CAD$ {formatAmount(cardTotals.earningsCad)}</p>
                <p className='fs-5 fw-600 mt-2'>Final Total : CAD$ {formatAmount(cardTotals.earningsCadFinal)}</p>
              </div>
            </div>

            {/* "Total Taxes" stays commented out. Updated to cardTotals so it
                still works if it is restored — the old calculateTotalEarnings it
                used to call no longer exists.

                NO USD line, exactly as the E-Wallet page's tax card has none:
                GST/HST is charged on the BUYER's province and a buyer in a
                Canadian province is billed in CAD, so the USD bucket is 0.00 in
                every dataset — which is also why "Tax Price In USD" is commented
                out of the table above. Final Total still sums every row's CAD
                mirror, so a taxed non-CAD row would show up as Final Total
                disagreeing with CAD Total rather than as money going missing.
            <div className='col-lg-2 col-md-3 mt-3 p-4 card-container'>
              <div className='content card-content'>
                <p className='fs-5 fw-600'>Total Taxes</p>
                <p className='fs-6 fw-500 mt-3'>CAD Total: CAD$ {formatAmount(cardTotals.taxCad)}</p>
                <p className='fs-5 fw-600 mt-2'>Final Total : CAD$ {formatAmount(cardTotals.taxCadFinal)}</p>
              </div>
            </div>
            */}

            {/* Moved here from the E-Wallet page — the tax handed over with the
                released payouts; see taxProvided above. */}
            <div className='col-lg-2 col-md-3 mt-3 p-4 card-container'>
              <div className='content card-content'>
                <p className='fs-5 fw-600'>Total Tax Provided</p>
                <p className='fs-5 fw-500 mt-3'>CAD$ {formatAmount(taxProvided)}</p>
              </div>
            </div>

            {/* Brought up from the E-Wallet Balance page — earned but not yet
                releasable, i.e. the meeting ended less than 7 days ago; see
                pendingPayment above. */}
            <div className='col-lg-2 col-md-3 mt-3 p-4 card-container'>
              <div className='content card-content'>
                <p className='fs-5 fw-600'>Pending Payment</p>
                <p className='fs-6 fw-500 mt-3'>USD Total: US$ {formatAmount(pendingPayment.usd)}</p>
                <p className='fs-6 fw-500'>CAD Total: CAD$ {formatAmount(pendingPayment.cad)}</p>
                <p className='fs-5 fw-600 mt-2'>Final Total : CAD$ {formatAmount(pendingPayment.final)}</p>
              </div>
            </div>
          </div>

          <div>
            {/* flex-wrap: the six controls below add up to more than 12 grid
                columns once the gaps are counted, so without it the row ran off
                the right edge on a medium screen instead of wrapping. */}
            <div className="d-flex flex-wrap align-items-end mt-3 gap-3">
              <div className="col-md-2">
                <label htmlFor="daily-start-date" className="mb-2">Start Date:</label>
                <input
                  type="date"
                  name="start_date"
                  id="daily-start-date"
                  className="form-control"
                  required
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="col-md-2">
                <label htmlFor="daily-end-date" className="mb-2">End Date:</label>
                <input
                  type="date"
                  name="end_date"
                  id="daily-end-date"
                  className="form-control"
                  required
                  value={endDate}
                  min={startDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <div className="col-md-2">
                <label htmlFor="currency-select" className="mb-2">Currency:</label>
                <select
                  id="currency-select"
                  className="form-control"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="CAD">CAD</option>
                  <option value="USD">USD</option>
                </select>
              </div>

              <div className='col-md-3'>
                <label className="mb-2">Teacher / Organization:</label>
                <Select
                  options={teacherOrgOptions}
                  className="skip_pdf"
                  id="teacher-org-select"
                  value={selectedTeacherOrg}
                  onChange={(option) => setSelectedTeacherOrg(option)}
                  isClearable
                  // react-select keys `styles` by SUB-COMPONENT, so the bare
                  // { width, minHeight } this used to pass was silently ignored
                  // and the control kept its default height while the inputs
                  // beside it were 40px. Written as a control override, it
                  // actually applies and the row lines up.
                  styles={{
                    control: (base) => ({ ...base, minHeight: '40px' }),
                  }}
                  placeholder="All Teachers & Organizations"
                />
              </div>

              <div className="col-md-2">
                <label htmlFor="search-input" className="mb-2">Search:</label>
                <input
                  type="text"
                  id="search-input"
                  className="form-control"
                  placeholder="Search class name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* "Download" asks the API to render this table as a PDF
                  (template.admin_earning_invoice); "Excel" writes the SAME
                  statement as a .xlsx from the rows already on screen (see
                  excelColumns). No col-md-* here on purpose: the filter fields
                  above already claim 11 of the 12 columns, so the pair sizes to
                  its own content and wraps onto the next line instead of pushing
                  the row past the page edge. */}
              {startDate && endDate && earningData.length > 0 &&
                <div className="d-flex align-items-end gap-2 flex-shrink-0">
                  <button className="btn-theme bg-yellow" onClick={() => downloadPdf()}>Download</button>
                  <button className="btn-theme bg-yellow" onClick={() => downloadExcel()}>Excel</button>
                </div>
              }
            </div>

            <div id='download_earnings'>
              <div className='mt-3'>
                {earningData && earningData.length > 0 ? (
                  <DataTable columns={columns} data={earningData} keyField="id" pagination customStyles={customStyles} />
                ) : (
                  <p className="mt-5 text-center text-purple fw-500 fs-3">No data Found</p>
                )}
              </div>
            </div>
          </div>

        </div>
      </main>
    </>
  );
}